/* ============================================================
   Classy Customer Reviews  (<classy-customer-reviews>)
   True circular coverflow.
   - Cards are absolutely positioned; the active card sits in the
     center, with neighbors flanking it on BOTH sides at all times.
     The list wraps, so there is never an empty edge (requirement:
     "perfectly circular without any gaps").
   - Only the center video ever plays. Side videos show their poster.
   - Autoplay of the center video is a section setting (default off).
   - Click a side card  -> it becomes the center and plays.
   - Click the playing center -> pauses (+ play icon). Click play -> plays (loops).
   - Sound (one at a time) and open-in-new-tab live on the center card.
   - Arrows + swipe move the center by one, wrapping around.
   Lazy: a clip's <video> src is only set when it plays.
   ============================================================ */
(function () {
  'use strict';

  if (customElements.get('classy-customer-reviews')) return;

  function debounce(fn, wait) {
    var t;
    return function () {
      var ctx = this, args = arguments;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, wait);
    };
  }

  var VISIBLE = 2; // how many neighbors to show on each side

  class ClassyCustomerReviews extends HTMLElement {
    connectedCallback() {
      this.viewport = this.querySelector('[data-ccr-viewport]');
      this.track = this.querySelector('[data-ccr-track]');
      this.prevBtn = this.querySelector('[data-ccr-prev]');
      this.nextBtn = this.querySelector('[data-ccr-next]');
      this.cards = Array.prototype.slice.call(this.querySelectorAll('[data-ccr-card]'));

      this.autoplayCenter = this.getAttribute('data-autoplay-center') === 'true';
      this.unmuteOnClick = this.getAttribute('data-unmute-click') !== 'false';

      this.active = 0;
      this.unmuted = null;
      this.sectionInView = true;

      if (!this.track || !this.cards.length) return;

      this.bindArrows();
      this.bindCards();
      this.bindSwipe();
      this.observeSection();

      this._onResize = debounce(this.layout.bind(this), 120);
      window.addEventListener('resize', this._onResize);
      // Fonts loading can change caption height; re-measure when ready.
      if (document.fonts && document.fonts.ready) {
        var self = this;
        document.fonts.ready.then(function () { self.layout(); });
      }

      this.updateArrows();

      var self = this;
      requestAnimationFrame(function () {
        self.layout();
        if (self.autoplayCenter && self.sectionInView) self.playCard(self.cards[self.active], false);
      });
    }

    disconnectedCallback() {
      if (this.sectionObserver) this.sectionObserver.disconnect();
      window.removeEventListener('resize', this._onResize);
    }

    /* ---------- metrics ---------- */
    metrics() {
      var trackCS = getComputedStyle(this.track);
      var rootCS = getComputedStyle(this);
      var cardW = parseFloat(trackCS.getPropertyValue('--ccr-card-w')) || 300;
      var cardH = parseFloat(trackCS.getPropertyValue('--ccr-card-h')) || 440;
      var gap = parseFloat(rootCS.getPropertyValue('--ccr-gap')) || 24;
      var sideScale = parseFloat(rootCS.getPropertyValue('--ccr-side-scale')) || 0.8;
      var sideFade = parseFloat(rootCS.getPropertyValue('--ccr-side-fade')) || 0.55;
      return { cardW: cardW, cardH: cardH, gap: gap, sideScale: sideScale, sideFade: sideFade };
    }

    // Circular signed distance from the active index, in [-n/2, n/2].
    rel(i) {
      var n = this.cards.length;
      var r = i - this.active;
      if (r > n / 2) r -= n;
      if (r < -n / 2) r += n;
      return r;
    }

    // Horizontal offset for a card `steps` away from center (non-overlapping).
    offsetFor(steps, m) {
      if (steps === 0) return 0;
      var dir = steps > 0 ? 1 : -1;
      var a = Math.abs(steps);
      // center -> first neighbor: half the center + gap + half the (scaled) neighbor
      var x = m.cardW / 2 + m.gap + (m.cardW * m.sideScale) / 2;
      // each further neighbor: a full scaled card + gap
      for (var k = 2; k <= a; k++) x += m.cardW * m.sideScale + m.gap;
      return dir * x;
    }

    layout() {
      var m = this.metrics();
      var n = this.cards.length;
      for (var i = 0; i < n; i++) {
        var card = this.cards[i];
        var r = this.rel(i);
        var a = Math.abs(r);

        if (a > VISIBLE) {
          // Park just off the visible range on the correct side so it animates in nicely.
          card.classList.add('is-hidden');
          card.classList.remove('is-vis', 'is-active');
          card.style.setProperty('--ccr-x', this.offsetFor((r > 0 ? 1 : -1) * (VISIBLE + 1), m) + 'px');
          card.style.setProperty('--ccr-s', m.sideScale);
          card.style.setProperty('--ccr-o', '0');
          card.style.setProperty('--ccr-z', '0');
          continue;
        }

        card.classList.remove('is-hidden');
        card.classList.add('is-vis');
        var scale = r === 0 ? 1 : (a === 1 ? m.sideScale : m.sideScale * 0.88);
        var op = r === 0 ? 1 : (a === 1 ? m.sideFade : m.sideFade * 0.55);
        card.style.setProperty('--ccr-x', this.offsetFor(r, m) + 'px');
        card.style.setProperty('--ccr-s', scale);
        card.style.setProperty('--ccr-o', op);
        card.style.setProperty('--ccr-z', String(100 - a));
        card.classList.toggle('is-active', r === 0);
      }

      // Size the viewport to fit the center card + its caption + shadow room.
      var activeCard = this.cards[this.active];
      var info = activeCard ? activeCard.querySelector('.ccr__info') : null;
      var infoH = info ? info.offsetHeight : 0;
      if (this.viewport) this.viewport.style.minHeight = (m.cardH + infoH + 60) + 'px';
    }

    /* ---------- navigation ---------- */
    bindArrows() {
      var self = this;
      if (this.prevBtn) this.prevBtn.addEventListener('click', function () { self.step(-1); });
      if (this.nextBtn) this.nextBtn.addEventListener('click', function () { self.step(1); });
    }
    updateArrows() {
      var many = this.cards.length > 1;
      if (this.prevBtn) this.prevBtn.hidden = !many;
      if (this.nextBtn) this.nextBtn.hidden = !many;
    }
    step(dir) { this.setActive(this.active + dir, { play: this.autoplayCenter }); }

    setActive(i, opts) {
      opts = opts || {};
      var n = this.cards.length;
      i = ((i % n) + n) % n;
      var prev = this.active;
      if (i === prev) { this.layout(); return; }
      this.active = i;
      if (prev >= 0) this.resetCard(this.cards[prev]);
      this.layout();
      var play = (typeof opts.play === 'boolean') ? opts.play : this.autoplayCenter;
      if (play && this.sectionInView) this.playCard(this.cards[i], !!opts.withSound);
    }

    bindSwipe() {
      var self = this, el = this.viewport || this.track;
      var x0 = 0, y0 = 0;
      el.addEventListener('touchstart', function (e) {
        if (e.touches.length !== 1) return;
        x0 = e.touches[0].clientX; y0 = e.touches[0].clientY;
      }, { passive: true });
      el.addEventListener('touchend', function (e) {
        var dx = e.changedTouches[0].clientX - x0;
        var dy = e.changedTouches[0].clientY - y0;
        if (Math.abs(dx) < 40 || Math.abs(dx) <= Math.abs(dy)) return;
        self.step(dx < 0 ? 1 : -1);
      }, { passive: true });
    }

    observeSection() {
      var self = this;
      if (!('IntersectionObserver' in window)) { this.sectionInView = true; return; }
      this.sectionObserver = new IntersectionObserver(function (entries) {
        self.sectionInView = entries[0].isIntersecting;
        if (!self.sectionInView) {
          var c = self.cards[self.active];
          if (c && c._ccrVideo && !c._ccrVideo.paused) c._ccrVideo.pause();
        }
      }, { threshold: 0 });
      this.sectionObserver.observe(this);
    }

    /* ---------- cards / controls ---------- */
    bindCards() {
      var self = this;
      this.cards.forEach(function (card, index) {
        var media = card.querySelector('[data-ccr-media]');
        var type = media ? media.getAttribute('data-ccr-type') : 'hosted';
        var playBtn = card.querySelector('[data-ccr-play]');
        var soundBtn = card.querySelector('[data-ccr-sound]');
        var newtabBtn = card.querySelector('[data-ccr-newtab]');

        if (type === 'youtube' || type === 'vimeo') {
          var handleExternal = function (e) {
            if (e) e.stopPropagation();
            if (index !== self.active) { self.setActive(index, { play: false }); return; }
            self.loadEmbed(media);
          };
          if (media) media.addEventListener('click', handleExternal);
          if (playBtn) playBtn.addEventListener('click', handleExternal);
          if (newtabBtn) newtabBtn.addEventListener('click', function (e) { e.stopPropagation(); self.openNewTab(media); });
          return;
        }

        var video = card.querySelector('[data-ccr-video]');
        if (!video) return;
        card._ccrVideo = video;
        card._ccrMedia = media;

        video.addEventListener('playing', function () { media.classList.add('is-playing'); self.hideSpinner(media, video); });
        video.addEventListener('loadeddata', function () { self.hideSpinner(media, video); });
        video.addEventListener('canplay', function () { self.hideSpinner(media, video); });
        video.addEventListener('error', function () { self.hideSpinner(media, video); });
        video.addEventListener('waiting', function () { self.showSpinner(media, video); });
        video.addEventListener('pause', function () { self.hideSpinner(media, video); media.classList.remove('is-playing'); });

        var onClick = function (e) {
          if (e) e.stopPropagation();
          if (index !== self.active) self.setActive(index, { play: true, withSound: self.unmuteOnClick });
          else self.togglePlay(card);
        };
        if (media) media.addEventListener('click', onClick);
        if (playBtn) playBtn.addEventListener('click', onClick);
        if (soundBtn) soundBtn.addEventListener('click', function (e) { e.stopPropagation(); self.toggleSound(video, media); });
        if (newtabBtn) newtabBtn.addEventListener('click', function (e) { e.stopPropagation(); self.openNewTab(media); });
      });
    }

    ensureSrc(video) {
      if (video.dataset.ccrLoaded === 'true') return;
      var src = video.getAttribute('data-src');
      if (src) video.src = src;
      video.dataset.ccrLoaded = 'true';
    }

    playCard(card, withSound) {
      if (!card || !card._ccrVideo) return;
      var video = card._ccrVideo, media = card._ccrMedia;
      this.ensureSrc(video);
      if (withSound && this.unmuteOnClick) this.unmute(video, media);
      else if (video !== this.unmuted) video.muted = true;
      if (media && !media.classList.contains('is-playing')) this.showSpinner(media, video);
      var p = video.play();
      if (p && p.catch) p.catch(function () {});
    }

    togglePlay(card) {
      if (!card || !card._ccrVideo) return;
      var video = card._ccrVideo;
      // Clicking the card / play icon starts the video WITH sound. Only the
      // mute icon mutes it. (Gated by the "sound on click" setting, default on.)
      if (video.paused) this.playCard(card, true);
      else video.pause();
    }

    resetCard(card) {
      if (!card || !card._ccrVideo) return;
      var video = card._ccrVideo, media = card._ccrMedia;
      if (!video.paused) video.pause();
      try { video.currentTime = 0; } catch (e) {}
      video.muted = true;
      if (this.unmuted === video) this.unmuted = null;
      if (media) { media.classList.remove('is-playing'); media.classList.remove('is-unmuted'); }
    }

    showSpinner(media, video) {
      if (!media) return;
      media.classList.add('is-loading');
      clearTimeout(video._spin);
      video._spin = setTimeout(function () { media.classList.remove('is-loading'); }, 7000);
    }
    hideSpinner(media, video) {
      if (!media) return;
      media.classList.remove('is-loading');
      clearTimeout(video._spin);
    }

    unmute(video, media) {
      this.muteOthers(video);
      this.unmuted = video;
      video.muted = false;
      if (media) media.classList.add('is-unmuted');
    }
    toggleSound(video, media) {
      if (video.muted) {
        this.unmute(video, media);
        if (video.paused) this.playCard(this.cards[this.active], false);
      } else {
        video.muted = true;
        if (this.unmuted === video) this.unmuted = null;
        if (media) media.classList.remove('is-unmuted');
      }
    }
    muteOthers(except) {
      this.cards.forEach(function (card) {
        var v = card._ccrVideo;
        if (v && v !== except) { v.muted = true; if (card._ccrMedia) card._ccrMedia.classList.remove('is-unmuted'); }
      });
    }

    openNewTab(media) {
      var url = media && media.getAttribute('data-open-url');
      if (url) window.open(url, '_blank', 'noopener');
    }

    loadEmbed(media) {
      if (!media || media.dataset.ccrEmbedded === 'true') return;
      var url = media.getAttribute('data-embed');
      if (!url) return;
      var iframe = document.createElement('iframe');
      iframe.className = 'ccr__embed';
      iframe.setAttribute('src', url);
      iframe.setAttribute('allow', 'autoplay; encrypted-media; picture-in-picture; fullscreen');
      iframe.setAttribute('allowfullscreen', '');
      iframe.setAttribute('title', media.getAttribute('data-title') || 'Customer video');
      media.appendChild(iframe);
      media.classList.add('is-playing');
      media.dataset.ccrEmbedded = 'true';
    }
  }

  customElements.define('classy-customer-reviews', ClassyCustomerReviews);
})();
